import { APP_TERMS_VERSION } from "@/content/productLegal";
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { protectedRouteRender } from '@/lib/protectedRouteGate';
import { destinoAposLoginDaRota } from '@/lib/authReturnTarget';
import { supabase } from '@/integrations/supabase/client';
import { TermsModal } from '@/components/TermsModal';
import AppBootSkeleton from '@/components/AppBootSkeleton';
import { AppBootError } from '@/components/AppBootError';
import { registerCacheClear } from '@/lib/cache';
import {
  completeAppOpen,
  getAppOpenRemainingMs,
  restartAppOpenAttempt,
  startAppOpenStage,
} from '@/lib/appOpenTelemetry';

// Flags globais para evitar re-verificações desnecessárias
let consentVerifiedThisSession = false;
let consentResultCache: boolean | null = null;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, bootstrapError, retryBootstrap } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const postLoginReturnTo = destinoAposLoginDaRota(location);
  const authId = user?.id ?? null;

  const [needsConsent, setNeedsConsent] = useState<boolean | null>(consentResultCache);
  const [consentError, setConsentError] = useState(false);
  const [consentAttempt, setConsentAttempt] = useState(0);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', {
        replace: true,
        state: { postLoginReturnTo },
      });
    }
  }, [user, loading, navigate, postLoginReturnTo]);

  // Consent check — bloqueia children até consentimento ser verificado/aceito
  useEffect(() => {
    if (consentVerifiedThisSession) {
      setNeedsConsent(consentResultCache);
      return;
    }

    if (loading || !authId) return;

    let cancelled = false;
    const controller = new AbortController();
    const stage = startAppOpenStage('bootstrap.consent');
    const timer = setTimeout(
      () => controller.abort(),
      getAppOpenRemainingMs(8000),
    );
    setConsentError(false);
    setNeedsConsent(null);

    void (async () => {
      try {
        const { data, error } = await supabase
          .from('user_consents')
          .select('id')
          .eq('auth_id', authId)
          .eq('terms_version', APP_TERMS_VERSION)
          .limit(1)
          .abortSignal(controller.signal)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') throw error;
        if (cancelled) {
          stage.end('cancelled');
          return;
        }

        const needsNewConsent = !data;
        consentResultCache = needsNewConsent;
        consentVerifiedThisSession = true;
        setNeedsConsent(needsNewConsent);
        stage.end('ok');
      } catch {
        if (cancelled) {
          stage.end('cancelled');
          return;
        }
        setConsentError(true);
        setNeedsConsent(null);
        stage.end(controller.signal.aborted ? 'timeout' : 'error');
        console.warn('[ProtectedRoute] consentimento indisponível');
      } finally {
        clearTimeout(timer);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [authId, consentAttempt, loading]);

  const retryConsent = () => {
    restartAppOpenAttempt();
    setConsentAttempt((value) => value + 1);
  };

  const retryAuth = () => {
    retryBootstrap();
  };

  const handleConsentAccepted = () => {
    consentResultCache = false;
    setNeedsConsent(false);
  };

  // 2026-05-26 Thiago feedback: auth + consent check pode levar 1-3s e o
  // usuário via tela branca durante esse tempo (SimpleLoading era um div
  // vazio "intentionally blank"). Trocado pelo AppBootSkeleton — feedback
  // visual imediato com bones animados em vez de tela vazia.
  //
  // AppBootSkeleton é genérico (sem tabs específicas da Home) — funciona
  // tanto pra /, /chat, /profile, /meu-progresso etc. SimpleLoading mantém
  // intacto pros outros usos (Login.tsx, MealDetail.tsx, Suspense fallbacks).
  // Decisão de render centralizada e PURA (testável dependency-free) — ver protectedRouteGate.ts.
  // Loader NEUTRO escuro pra quem vai pra anamnese (mata o flash do "skeleton do App" com cara de Home);
  // AppBootSkeleton pros demais; Termos SEMPRE antes da anamnese (LGPD).
  const decision = protectedRouteRender({
    authLoading: loading,
    hasUser: !!user,
    needsConsent,
  });

  useEffect(() => {
    if (bootstrapError || consentError) {
      completeAppOpen('bootstrap.unavailable', 'unavailable');
    } else if (decision === 'terms') {
      completeAppOpen('terms', 'terms');
    }
  }, [bootstrapError, consentError, decision]);

  if (bootstrapError) {
    return <AppBootError onRetry={retryAuth} />;
  }
  if (consentError) {
    return <AppBootError onRetry={retryConsent} />;
  }

  switch (decision) {
    case 'none':
      return null;
    case 'terms':
      return <TermsModal onAccept={handleConsentAccepted} />;
    case 'loader-skeleton':
      return <AppBootSkeleton />;
    default:
      return <>{children}</>;
  }
};

// Função para limpar caches no logout
export const clearAuthCaches = () => {
  consentVerifiedThisSession = false;
  consentResultCache = null;
};

registerCacheClear(clearAuthCaches);
